import { Button, Input } from "antd";
import { useGetPromotionCodeList } from "services/promotionCode";
import PromotionCodeItem from "./PromotionCodeItem";
import { FC, useState } from "react";
import { TicketPercent } from "lucide-react";

interface CartItem {
  id: number;
  productId: number;
  price: string;
  qty: number;
  isRedemptionItem?: boolean;
}

interface IPromotionCodeDrawer {
  onClose: () => void;
  sum: number;
  applyPromotionCode: (selected: boolean, code: string) => void;
  selectedPromotionCode?: string;
  productIds?: number[];
  cartItems?: CartItem[];
}
const PromotionCodeDrawer: FC<IPromotionCodeDrawer> = ({
  onClose,
  sum,
  applyPromotionCode,
  selectedPromotionCode,
  productIds = [],
  cartItems = [],
}) => {
  const { promotionCodeList } = useGetPromotionCodeList();
  const [inputCode, setInputCode] = useState("");

  // Helper function to calculate applicable sum based on promotion products
  const calculateApplicableSum = (promotionProducts: { id: number }[]) => {
    // If no specific products, use total sum (applicable to all products)
    if (!promotionProducts || promotionProducts.length === 0) {
      return sum;
    }

    // Calculate sum only for products that match promotion
    const applicableProductIds = promotionProducts.map((p) => p.id);
    return cartItems
      .filter((item) => applicableProductIds.includes(item.productId))
      .reduce((total, item) => total + parseFloat(item.price) * item.qty, 0);
  };

  // Filter and categorize promotion codes
  const applicablePromotions =
    promotionCodeList?.filter((promotionCode) => {
      // Calculate applicable sum based on promotion's products
      const applicableSum = calculateApplicableSum(promotionCode.products);

      // Check minimum order amount against applicable sum
      const minOrderMet = +(promotionCode.minOrderAmount || 0) <= applicableSum;

      // Check if promotion is product-specific and applicable
      const isProductSpecific =
        promotionCode.products && promotionCode.products.length > 0;
      const applicableProductIds = promotionCode.products
        ? promotionCode.products.map((p) => p.id)
        : [];
      const hasApplicableProducts =
        !isProductSpecific ||
        applicableProductIds.some((id) => productIds.includes(id));

      return minOrderMet && (!isProductSpecific || hasApplicableProducts);
    }) || [];

  const nonApplicablePromotions =
    promotionCodeList?.filter((promotionCode) => {
      // Calculate applicable sum based on promotion's products
      const applicableSum = calculateApplicableSum(promotionCode.products);

      // Check minimum order amount against applicable sum
      const minOrderMet = +(promotionCode.minOrderAmount || 0) <= applicableSum;

      // Check if promotion is product-specific and not applicable
      const isProductSpecific =
        promotionCode.products && promotionCode.products.length > 0;
      const applicableProductIds = promotionCode.products
        ? promotionCode.products.map((p) => p.id)
        : [];
      const hasApplicableProducts =
        !isProductSpecific ||
        applicableProductIds.some((id) => productIds.includes(id));

      return !minOrderMet || (isProductSpecific && !hasApplicableProducts);
    }) || [];

  const handleSelectPromotionCode = async (selected: boolean, code: string) => {
    applyPromotionCode(selected, code);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setInputCode(e.target.value);
  };

  const handleApplyCode = () => {
    if (inputCode.trim()) {
      applyPromotionCode(false, inputCode.trim());
      onClose();
    }
  };

  return (
    <div className="flex flex-col justify-between h-full">
      <div>
        <div>
          <div className="flex gap-4">
            <Input
              placeholder="โค้ดส่วนลด"
              value={inputCode}
              onChange={handleInputChange}
              onPressEnter={handleApplyCode}
            />
            <Button
              type="primary"
              className="px-8 py-5"
              onClick={handleApplyCode}
              disabled={!inputCode.trim()}
            >
              ใช้
            </Button>
          </div>
        </div>
        <div className="mt-8">
          {!promotionCodeList || promotionCodeList.length === 0 ? (
            <div className="text-center flex justify-center flex-col items-center">
              <TicketPercent size={40} color="gray" />
              <div className="font-semibold">ไม่มีโค้ดส่วนลดที่ใช้ได้</div>
            </div>
          ) : (
            <>
              {applicablePromotions.length > 0 && (
                <>
                  <div className="text-green-600 font-semibold">
                    โค้ดส่วนลดที่ใช้ได้
                  </div>
                  <div className="text-sm text-gray-600 mb-4">
                    เลือกได้ 1 ส่วนลด
                  </div>
                  <div className="flex flex-col gap-4 mb-6">
                    {applicablePromotions.map((promotionCode) => (
                      <PromotionCodeItem
                        key={promotionCode.id}
                        data={promotionCode}
                        isSelected={
                          promotionCode.code === selectedPromotionCode
                        }
                        onSelect={handleSelectPromotionCode}
                        isDisabled={false}
                        productIds={productIds}
                        isProductSpecificDisabled={false}
                      />
                    ))}
                  </div>
                </>
              )}

              {nonApplicablePromotions.length > 0 && (
                <>
                  <div className="text-gray-500 font-semibold">
                    โค้ดส่วนลดที่ไม่สามารถใช้ได้ (
                    {nonApplicablePromotions.length})
                  </div>
                  <div className="text-sm text-gray-500 mb-4">
                    {productIds.length === 0
                      ? "ไม่มีสินค้าในตะกร้า หรือไม่ถึงยอดขั้นต่ำ"
                      : "ไม่ถึงยอดขั้นต่ำ หรือไม่ใช่สินค้าที่กำหนด"}
                  </div>
                  <div className="flex flex-col gap-4">
                    {nonApplicablePromotions.map((promotionCode) => {
                      const applicableSum = calculateApplicableSum(
                        promotionCode.products
                      );
                      const isMinOrderNotMet =
                        +(promotionCode.minOrderAmount || 0) > applicableSum;
                      const isProductSpecific =
                        promotionCode.products &&
                        promotionCode.products.length > 0;
                      const applicableProductIds = promotionCode.products
                        ? promotionCode.products.map((p) => p.id)
                        : [];
                      const hasApplicableProducts =
                        !isProductSpecific ||
                        applicableProductIds.some((id) =>
                          productIds.includes(id)
                        );

                      return (
                        <PromotionCodeItem
                          key={promotionCode.id}
                          data={promotionCode}
                          isSelected={false}
                          onSelect={() => {}} // No action for disabled items
                          isDisabled={isMinOrderNotMet}
                          productIds={productIds}
                          isProductSpecificDisabled={
                            isProductSpecific && !hasApplicableProducts
                          }
                        />
                      );
                    })}
                  </div>
                </>
              )}
            </>
          )}
        </div>
      </div>
      <div className="py-4 border-t border-gray-100">
        <Button block type="primary" size="large" onClick={onClose}>
          ยืนยัน
        </Button>
      </div>
    </div>
  );
};
export default PromotionCodeDrawer;
